/**
 * ════════════════════════════════════════════════════
 *   Blog Auto Slider — JavaScript
 *   File: assets/blog-auto-slider.js
 *
 *   Add to theme.liquid before </body>:
 *   {{ 'blog-auto-slider.js' | asset_url | script_tag }}
 *
 *   Features:
 *   ✓ Reads <!-- SLIDER:blog-auto-slider --> comment in
 *     blog post content and injects the slider there
 *   ✓ Multiple <!-- SLIDER:blog-auto-slider --> comments
 *     in same post each get their own independent slider
 *   ✓ Auto-scrolls continuously (left to right, loops)
 *   ✓ Pauses on mouse hover / touch
 *   ✓ Prev / Next buttons
 *   ✓ Smooth infinite loop (clones first/last cards)
 *   ✓ Keyboard accessible
 * ════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────
     CONFIG
  ───────────────────────────────────────────────── */
  var CONFIG = {
    commentMarker:   'SLIDER:blog-auto-slider',
    autoScrollSpeed: 0.6,    // px per animation frame (~36px/sec at 60fps)
    autoScrollDelay: 800,    // ms to wait before auto-scroll starts after pause
    cardSelector:    '.bas__card',
    trackSelector:   '[data-bas-track]',
    prevSelector:    '[data-bas-prev]',
    nextSelector:    '[data-bas-next]',
    sliderSelector:  '[data-bas]',
  };

  /* ─────────────────────────────────────────────────
     STEP 1 — Inject slider(s) from HTML comment(s)

     UPDATED: Uses TreeWalker to collect ALL comment
     nodes first, then injects a unique independent
     clone at each position. Supports multiple
     <!-- SLIDER:blog-auto-slider --> in one post.
  ───────────────────────────────────────────────── */
  function injectFromComments() {
   var articleContent = document.querySelector(
  '.article__content, .article-template__content, .article-template__content.rte, [data-article-content], .rte, .article-body, .article__body, .post-content, .entry-content'
);
    if (!articleContent) return;

    // Find the source slider template rendered by Liquid at bottom of article
    var sourceSlider = document.querySelector('[data-bas]');
    if (!sourceSlider) return;

    // Use TreeWalker to find ALL comment nodes inside article content
    // (safer than childNodes loop — handles nested elements too)
    var commentNodes = [];
    var walker = document.createTreeWalker(
      articleContent,
      NodeFilter.SHOW_COMMENT,
      null,
      false
    );

    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim().indexOf('SLIDER:blog-auto-slider') !== -1) {
        commentNodes.push(node);
      }
    }

    // No comments found — nothing to inject
    if (!commentNodes.length) return;

    // Inject a fresh independent clone at each comment position
    commentNodes.forEach(function (commentNode, index) {
      var clone = sourceSlider.cloneNode(true);

      // Each slider gets a unique ID and fresh data attribute
      // so initSlider() treats them as fully independent instances
      clone.removeAttribute('style');
      clone.style.display = '';
      clone.setAttribute('id', 'blog-auto-slider-' + (index + 1));
      clone.setAttribute('data-bas', '');
      clone.setAttribute('data-bas-instance', index + 1);

      commentNode.parentNode.insertBefore(clone, commentNode);
      commentNode.parentNode.removeChild(commentNode);
    });

    // Hide the original template source — it was only used for cloning
    sourceSlider.style.display = 'none';
  }

  /* ─────────────────────────────────────────────────
     STEP 2 — Init all sliders on page
  ───────────────────────────────────────────────── */
  function initAll() {
    var sliders = document.querySelectorAll(CONFIG.sliderSelector);
    sliders.forEach(function (el) {
      if (el.style.display === 'none') return; // skip hidden template source
      initSlider(el);
    });
  }

  /* ─────────────────────────────────────────────────
     STEP 3 — Single slider initialisation
  ───────────────────────────────────────────────── */
  function initSlider(slider) {
    var track   = slider.querySelector(CONFIG.trackSelector);
    var prevBtn = slider.querySelector(CONFIG.prevSelector);
    var nextBtn = slider.querySelector(CONFIG.nextSelector);

    if (!track) return;

    var cards = Array.from(track.querySelectorAll(CONFIG.cardSelector));
    if (cards.length < 2) return;

    /* ── Clone cards for seamless infinite loop ── */
    var visibleCount = getVisibleCount(track, cards);
    var clonesBefore = cards.slice(-visibleCount).map(function (c) {
      var cl = c.cloneNode(true);
      cl.setAttribute('aria-hidden', 'true');
      cl.classList.add('bas__card--clone');
      return cl;
    });
    var clonesAfter = cards.slice(0, visibleCount).map(function (c) {
      var cl = c.cloneNode(true);
      cl.setAttribute('aria-hidden', 'true');
      cl.classList.add('bas__card--clone');
      return cl;
    });

    clonesBefore.forEach(function (cl) { track.insertBefore(cl, track.firstChild); });
    clonesAfter.forEach(function (cl) { track.appendChild(cl); });

    /* ── Set initial scroll position (past the clones) ── */
    var cardWidth   = cards[0].offsetWidth;
    var gap         = parseInt(getComputedStyle(track).gap || 28);
    var cardStep    = cardWidth + gap;
    var startOffset = clonesBefore.length * cardStep;

    track.style.transform = 'translateX(-' + startOffset + 'px)';

    /* ── State ── */
    var currentOffset   = startOffset;
    var totalCards      = cards.length;
    var isTransitioning = false;
    var autoTimer       = null;
    var rafId           = null;
    var isPaused        = false;

    /* ── Smooth slide function ── */
    function slideTo(offset, animate) {
      if (animate !== false) {
        track.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)';
      } else {
        track.style.transition = 'none';
      }
      track.style.transform = 'translateX(-' + offset + 'px)';
      currentOffset = offset;
    }

    /* ── After transition: jump silently for infinite loop ── */
    track.addEventListener('transitionend', function () {
      isTransitioning = false;
      var maxOffset = startOffset + (totalCards * cardStep);
      var minOffset = startOffset;

      if (currentOffset >= maxOffset) {
        slideTo(minOffset, false);
      }
      if (currentOffset <= minOffset - cardStep) {
        slideTo(maxOffset - cardStep, false);
      }
    });

    /* ── Next / Prev ── */
    function next() {
      if (isTransitioning) return;
      isTransitioning = true;
      slideTo(currentOffset + cardStep);
    }

    function prev() {
      if (isTransitioning) return;
      isTransitioning = true;
      slideTo(currentOffset - cardStep);
    }

    if (nextBtn) nextBtn.addEventListener('click', function () { pauseAuto(); next(); resumeAutoAfterDelay(); });
    if (prevBtn) prevBtn.addEventListener('click', function () { pauseAuto(); prev(); resumeAutoAfterDelay(); });

    /* ── Keyboard ── */
    slider.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { pauseAuto(); prev(); resumeAutoAfterDelay(); }
      if (e.key === 'ArrowRight') { pauseAuto(); next(); resumeAutoAfterDelay(); }
    });

    /* ── Auto scroll (RAF-based, continuous) ── */
    var accumulator = 0;

    function autoScrollStep() {
      if (!isPaused) {
        accumulator += CONFIG.autoScrollSpeed;
        if (accumulator >= cardStep) {
          accumulator = 0;
          next();
        } else {
          track.style.transition = 'none';
          track.style.transform  = 'translateX(-' + (currentOffset + accumulator) + 'px)';
        }
      }
      rafId = requestAnimationFrame(autoScrollStep);
    }

    function startAuto() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(autoScrollStep);
    }

    function pauseAuto() {
      isPaused = true;
      if (autoTimer) { clearTimeout(autoTimer); autoTimer = null; }
    }

    function resumeAutoAfterDelay() {
      autoTimer = setTimeout(function () {
        isPaused = false;
      }, CONFIG.autoScrollDelay);
    }

    /* ── Pause on hover / touch ── */
    slider.addEventListener('mouseenter', pauseAuto);
    slider.addEventListener('mouseleave', function () { isPaused = false; });
    slider.addEventListener('touchstart', pauseAuto, { passive: true });
    slider.addEventListener('touchend', resumeAutoAfterDelay, { passive: true });

    /* ── Pause when tab is hidden ── */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { pauseAuto(); } else { isPaused = false; }
    });

    /* ── Start ── */
    startAuto();
  }

  /* ─────────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────────── */
  function getVisibleCount(track, cards) {
    if (!cards.length) return 3;
    var trackWidth = track.offsetWidth;
    var cardWidth  = cards[0].offsetWidth;
    var gap        = parseInt(getComputedStyle(track).gap || 28);
    return Math.ceil(trackWidth / (cardWidth + gap)) + 1;
  }

  /* ─────────────────────────────────────────────────
     BOOT
  ───────────────────────────────────────────────── */
  function boot() {
    injectFromComments();
    // Small delay to allow injected DOM to settle
    setTimeout(initAll, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
